import { Capacitor } from '@capacitor/core'
import { initializeApp, FirebaseApp } from 'firebase/app'
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  Auth,
  connectAuthEmulator,
} from 'firebase/auth'
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions'
import { getStorage, type FirebaseStorage, connectStorageEmulator } from 'firebase/storage'
import { getPerformance, type FirebasePerformance } from 'firebase/performance'
import { logger } from './logger'

// Firebase configuration validation
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const

// Validate environment variables
const missingVars = requiredEnvVars.filter(
  (varName) => !import.meta.env[varName]
)

if (missingVars.length > 0) {
  const errorMsg = `Missing Firebase configuration: ${missingVars.join(', ')}`
  logger.error(errorMsg)
  throw new Error(errorMsg)
}

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// Initialize Firebase with error handling
let app: FirebaseApp
try {
  app = initializeApp(firebaseConfig)
  logger.log('Firebase initialized successfully')
} catch (error) {
  logger.error('Firebase initialization failed', error)
  throw new Error(`Firebase initialization failed: ${error}`)
}

// Initialize services with error handling
let auth: Auth
let db: Firestore
let functions: Functions
let storage: FirebaseStorage
let perf: FirebasePerformance | null = null

try {
  // 네이티브(WKWebView)에서는 getAuth()의 기본 persistence 체인 + popup/redirect
  // resolver가 초기화 단계에서 멈춰(onAuthStateChanged 미응답) signInWithCredential까지
  // 무한 대기시킨다. indexedDBLocalPersistence로 명시 초기화해 회피.
  auth = Capacitor.isNativePlatform()
    ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
    : getAuth(app)
  db = getFirestore(app)
  functions = getFunctions(app, 'asia-northeast3') // Seoul region
  // 영수증 사진 업로드용. CSV는 Callable을 거치므로 Storage를 쓰지 않았다.
  storage = getStorage(app)
  logger.log('Firebase services initialized')
} catch (error) {
  logger.error('Firebase services initialization failed', error)
  throw new Error(`Firebase services initialization failed: ${error}`)
}

// Initialize Performance Monitoring separately (non-fatal — iOS WebView may not support it)
if (!import.meta.env.DEV) {
  try {
    perf = getPerformance(app)
    logger.log('Firebase Performance Monitoring initialized')
  } catch (error) {
    logger.warn('Firebase Performance Monitoring not available:', error)
  }
}

// Connect to Firebase Emulators in development mode
if (import.meta.env.DEV) {
  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:4043', { disableWarnings: true })
    connectFirestoreEmulator(db, '127.0.0.1', 4042)
    connectFunctionsEmulator(functions, '127.0.0.1', 4041)
    connectStorageEmulator(storage, '127.0.0.1', 4044)
    logger.log('Connected to Firebase Emulators')
  } catch (error) {
    logger.warn('Firebase Emulator connection failed:', error)
  }
}

export { app, auth, db, functions, storage, perf }
